/**
 * The module-completion toast, made competence-aware.
 *
 * "Training complete" is only half-true on a gated track: the learning is
 * done, but the track stays open until real production numbers clear its
 * competence gate(s). When the module just finished leaves a containing
 * track in exactly that state, the toast says so — you've done the
 * learning, now do the thing — naming the measure and the live count so
 * the learner knows what "the thing" is.
 *
 * The my-path fetch doubles as the freshness source: reading my-path runs
 * the server-side gate evaluation, so the counts in the toast are current
 * (and the fetch warms the cache the finish flow just invalidated).
 */

import type { QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { getMyPath, type MyPath, type PathGate } from "@/services/training-tracks";

export interface OutstandingProof {
  trackTitle: string;
  gates: PathGate[];
}

/** Tracks containing the module whose required learning is complete but
 *  whose competence gates are not — the "now do the thing" set. */
export function outstandingProofForModule(path: MyPath, moduleId: string): OutstandingProof[] {
  const out: OutstandingProof[] = [];
  for (const track of path.tracks) {
    for (const level of track.levels) {
      if (!level.modules.some((m) => m.module_id === moduleId)) continue;
      const learningDone = level.modules.every((m) => !m.required || m.completed);
      const unmet = (level.gates ?? []).filter((g) => !g.met);
      if (learningDone && unmet.length > 0) {
        out.push({ trackTitle: track.title, gates: unmet });
      }
    }
  }
  return out;
}

export function proofDescription(proof: OutstandingProof): string {
  const gates = proof.gates
    .map((g) => (g.threshold != null ? `${g.label} — ${g.value ?? 0} of ${g.threshold} so far` : g.label))
    .join("; ");
  return `You've done the learning — now do the thing. To complete “${proof.trackTitle}”, prove your competence in production: ${gates}.`;
}

/** The standard completion toast for every module-finish path. Falls back to
 *  the plain celebration whenever my-path can't be read — a proof nudge is
 *  never worth eating the completion moment. */
export async function announceTrainingComplete(queryClient: QueryClient, moduleId: string): Promise<void> {
  let proofs: OutstandingProof[] = [];
  try {
    const path = await queryClient.fetchQuery({ queryKey: ["training", "my-path"], queryFn: getMyPath });
    proofs = outstandingProofForModule(path, moduleId);
  } catch {
    // fall through to the plain toast
  }
  if (proofs.length === 0) {
    toast.success("Training complete — nice work!");
    return;
  }
  // A module can sit in several gated tracks; the first is the headline —
  // the hub's Gate outstanding badges carry the rest.
  toast.success("Learning complete — nice work!", {
    description: proofDescription(proofs[0]),
    duration: 9000,
  });
}
